import { Fragment, useState } from 'react';

import { CaregiverDetailModal } from '@apps/web/components/caregivers/caregiver_detail_modal';
import { ClientDetailModalById } from '@apps/web/components/clients/client_detail_modal_by_id';
import { Badge } from '@apps/web/components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { Markdown } from '@apps/web/components/ui/markdown';
import { Spinner } from '@apps/web/components/ui/spinner';
import { useAgentPlaybookItems } from '@apps/web/swr_data_fetching/data_fetching_hooks';

type OpenEntity =
    | { readonly kind: 'caregiver' | 'playbook'; readonly id: string }
    | {
          readonly kind: 'client';
          readonly id: string;
          readonly initialTab?: 'memory' | 'care-plan' | 'activity';
      };

function NotePlaybookDetailModal({
    organizationId,
    playbookId,
    onClose,
}: {
    readonly organizationId: string;
    readonly playbookId: string;
    readonly onClose: () => void;
}) {
    const { data, error, hasRequestFailed, isLoading } = useAgentPlaybookItems(
        organizationId,
        {
            reason: 'admin-agent-note-playbook-modal',
        },
    );
    const playbook = data?.items.find((item) => item.id === playbookId) ?? null;

    return (
        <Dialog
            open={true}
            onOpenChange={(next) => {
                if (!next) {
                    onClose();
                }
            }}
        >
            <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-3xl">
                <DialogHeader>
                    <DialogTitle>
                        {playbook?.title ?? 'Playbook rule'}
                    </DialogTitle>
                    <DialogDescription>
                        {playbook?.trigger_description ??
                            'The playbook rule created from this note.'}
                    </DialogDescription>
                </DialogHeader>
                {isLoading ? (
                    <div
                        role="status"
                        className="text-muted-foreground flex items-center gap-2 py-6 text-sm"
                    >
                        <Spinner size="sm" tone="muted" />
                        Loading playbook…
                    </div>
                ) : error !== undefined ||
                  (hasRequestFailed && data === undefined) ? (
                    <p className="text-destructive py-4 text-sm">
                        This playbook couldn’t be loaded.
                    </p>
                ) : playbook === null ? (
                    <p className="text-muted-foreground py-4 text-sm">
                        This playbook is no longer available.
                    </p>
                ) : (
                    <div className="space-y-4">
                        <div className="flex flex-wrap items-center gap-2">
                            <Badge
                                variant={
                                    playbook.enabled ? 'secondary' : 'outline'
                                }
                            >
                                {playbook.enabled ? 'On' : 'Off'}
                            </Badge>
                            {playbook.applies_to.map((surface) => (
                                <Badge
                                    key={surface}
                                    variant="outline"
                                    className="capitalize"
                                >
                                    {surface.split('_').join(' ')}
                                </Badge>
                            ))}
                        </div>
                        <div className="bg-muted/20 rounded-md border p-4">
                            <Markdown>{playbook.body_markdown}</Markdown>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

/**
 * Hosts note destination details so an admin can inspect caregivers, clients,
 * and playbook rules without leaving the notes page. Returns imperative
 * openers plus the modal elements to render. Each modal is mounted only while
 * open so its heavy detail component and fetches stay idle.
 */
export function useNoteEntityModals(organizationId: string) {
    const [open, setOpen] = useState<OpenEntity | null>(null);
    const close = () => {
        setOpen(null);
    };

    const entityModals = (
        <Fragment>
            {open?.kind === 'caregiver' && (
                <CaregiverDetailModal
                    isOpen={true}
                    onClose={close}
                    organizationId={organizationId}
                    caregiverId={open.id}
                />
            )}
            {open?.kind === 'client' && (
                <ClientDetailModalById
                    // Remount per client/tab: the modal applies initialTab
                    // via Tabs defaultValue, which is read only on mount.
                    key={`${open.id}-${open.initialTab ?? 'default'}`}
                    organizationId={organizationId}
                    clientId={open.id}
                    initialTab={open.initialTab}
                    open={true}
                    onOpenChange={(next) => {
                        if (!next) {
                            close();
                        }
                    }}
                />
            )}
            {open?.kind === 'playbook' && (
                <NotePlaybookDetailModal
                    organizationId={organizationId}
                    playbookId={open.id}
                    onClose={close}
                />
            )}
        </Fragment>
    );

    return {
        openCaregiver: (id: string) => {
            setOpen({ kind: 'caregiver', id });
        },
        openClient: (
            id: string,
            initialTab?: 'memory' | 'care-plan' | 'activity',
        ) => {
            setOpen({ kind: 'client', id, initialTab });
        },
        openPlaybook: (id: string) => {
            setOpen({ kind: 'playbook', id });
        },
        entityModals,
    };
}
